import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

// Button variant configurations using class-variance-authority
const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Light mode variants
        primary: "bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-sm",
        secondary: "bg-secondary-600 text-white hover:bg-secondary-700 active:bg-secondary-800 shadow-sm",
        success: "bg-success text-white hover:bg-green-600 active:bg-green-700 shadow-sm",
        danger: "bg-danger text-white hover:bg-red-600 active:bg-red-700 shadow-sm",
        
        // Dark mode variants
        "accent-gradient": "bg-accent-gradient text-white font-semibold hover:bg-accent-gradient-hover hover:shadow-glow active:scale-95 transition-all duration-200 shadow-lg",
        "accent": "bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700 dark:shadow-glow dark:hover:shadow-lg",
        "cyan": "bg-cyan-500 text-white hover:bg-cyan-600 active:bg-cyan-700 dark:shadow-cyan-glow dark:hover:shadow-lg",
        
        // Outline variants
        "primary-outline": "border border-primary-600 text-primary-600 hover:bg-primary-50 active:bg-primary-100 dark:border-accent-500 dark:text-accent-500 dark:hover:bg-accent-500/10",
        "secondary-outline": "border border-secondary-600 text-secondary-600 hover:bg-secondary-50 active:bg-secondary-100 dark:border-cyan-500 dark:text-cyan-500 dark:hover:bg-cyan-500/10",
        "gray-outline": "border border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100 dark:border-dark-300 dark:text-dark-700 dark:hover:bg-dark-200",
        "accent-outline": "border border-accent-500 text-accent-500 hover:bg-accent-500 hover:text-white active:bg-accent-600 transition-all duration-200",
        "cyan-outline": "border border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-white active:bg-cyan-600 transition-all duration-200",
        
        // Ghost variants
        ghost: "text-gray-700 hover:bg-gray-100 active:bg-gray-200 dark:text-dark-700 dark:hover:bg-dark-200",
        "ghost-primary": "text-primary-600 hover:bg-primary-50 active:bg-primary-100 dark:text-accent-500 dark:hover:bg-accent-500/10",
        "ghost-accent": "text-accent-500 hover:bg-accent-50 active:bg-accent-100 dark:hover:bg-accent-500/10",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        default: "h-10 px-4 py-2",
        lg: "h-12 px-6 text-base",
        "icon-sm": "h-8 w-8",
        icon: "h-10 w-10", 
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

// Icon size mapping
const iconSizes = {
  sm: "w-4 h-4",
  default: "w-5 h-5",
  lg: "w-6 h-6",
  icon: "w-5 h-5",
  "icon-sm": "w-4 h-4",
  "icon-lg": "w-6 h-6",
};

export const Button = React.forwardRef(({
  className,
  variant,
  size,
  children,
  leftIcon,
  rightIcon,
  loading = false,
  disabled = false,
  ...props
}, ref) => {
  const iconSize = iconSizes[size] || iconSizes.default;
  
  return (
    <button
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
          Loading...
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2 flex-shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2 flex-shrink-0">{rightIcon}</span>}
        </>
      )}
    </button>
  );
});

Button.displayName = "Button";

// Icon-only button component
export const IconButton = React.forwardRef(({
  className,
  variant = "ghost",
  size = "icon",
  children,
  ...props
}, ref) => {
  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={className}
      {...props}
    >
      {children}
    </Button>
  );
});

IconButton.displayName = "IconButton"; 